import React from 'react';
import isEmpty from 'lodash/isEmpty';
import usePortal from 'react-useportal';
import { useSelector, useDispatch } from 'react-redux';
import { getTransfersOfferType } from '../../../../../../service/utils';
import {
  CancelOfferPopup,
  RejectOfferPopup,
  ContractOfferPopup,
} from '../../../../../../components/Popup';
import { getPopupByName } from '../../../../../../redux/selector/Popups';
import { getDataForRequest } from '../../../../../../redux/selector/Transfers';
import { POPUPS } from '../../../../../../service/constants';

import {
  canRespondToOffer,
  respondToOffers,
} from '../../../../../../service/batchTransfers';
import { transfersListRequest } from '../../../../../../redux/actions/Transfers';
import {
  BatchBar,
  DealSelection,
  DealsHeading,
  SelectionToggle,
  SelectionCheck,
} from './styled';
import SwitchToggle from '../../../../../../components/SwitchToggle';
import { StyledButton } from '../../../../../../globalStyled';
import { StyledWrapper, StyledIcon, StyledLabel, StyledLink } from '../../styled';
import BatchOfferPopup from './BatchOfferPopup';

import TransfersPlayerCard from '../../../TransfersPlayerCard';
import { StyledContainer, StyledViewDealsContainer } from './styled';

const ViewDealsContainer = ({
  dealsList,
  selectPlayer,
  setSelectPlayer,
  teamId,
  allPlayersList,
  myTeam,
}) => {
  const dispatch = useDispatch();
  const [selectionMode, setSelectionMode] = React.useState(false);
  const [selected, setSelected] = React.useState([]);
  const [confirmation, setConfirmation] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const busyRef = React.useRef(false);
  const [progress, setProgress] = React.useState(0);
  const [results, setResults] = React.useState([]);
  const eligible = dealsList.filter(
    (offer) =>
      canRespondToOffer(offer, teamId) &&
      offer.player?._id &&
      offer.receiving_team &&
      [...allPlayersList, ...myTeam].some((player) => player._id === offer.player._id)
  );
  const selectedOffers = eligible.filter((offer) => selected.includes(offer._id));
  const multipleOffersForPlayer =
    new Set(selectedOffers.map((offer) => offer.player._id)).size !==
    selectedOffers.length;
  const toggle = (id) => {
    setConfirmation(null);
    setSelected((ids) =>
      ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id]
    );
  };
  React.useEffect(() => {
    const ids = new Set(eligible.map((offer) => offer._id));
    setSelected((old) =>
      old.every((id) => ids.has(id)) ? old : old.filter((id) => ids.has(id))
    );
  }, [dealsList, teamId, allPlayersList, myTeam]);
  const runBatch = async () => {
    if (
      busyRef.current ||
      !confirmation ||
      !selectedOffers.length ||
      (confirmation === 'accept' && multipleOffersForPlayer)
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setProgress(0);
    setResults([]);
    try {
      const outcome = await respondToOffers(selectedOffers, confirmation, setProgress);
      setResults(outcome);
      setSelected([]);
      setSelectionMode(false);
      await dispatch(transfersListRequest({ silent: true }));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const cancelOfferPopupName = 'cancelOfferPopup';
  const rejectOfferPopupName = 'rejectOfferPopup';
  const cancelOfferPopupData = useSelector(getPopupByName(cancelOfferPopupName));
  const rejectOfferPopupData = useSelector(getPopupByName(rejectOfferPopupName));
  const dataForPopup = useSelector(getDataForRequest);
  const contractOfferPopupData = useSelector(getPopupByName(POPUPS.CONTRACT_OFFER_POPUP));
  const contractPlayerId = contractOfferPopupData?.id;
  const contractPlayer =
    (contractPlayerId &&
      (allPlayersList.find((player) => player._id === contractPlayerId) ||
        myTeam.find((player) => player._id === contractPlayerId))) ||
    null;

  const { Portal } = usePortal({
    bindTo: document && document.querySelector('#modal'),
  });
  return (
    <StyledContainer>
      {contractOfferPopupData && (
        <Portal>
          <ContractOfferPopup
            key={contractOfferPopupData.id}
            popupName={POPUPS.CONTRACT_OFFER_POPUP}
            id={contractOfferPopupData.id}
            salary={contractPlayer?.salary}
          />
        </Portal>
      )}
      {cancelOfferPopupData && !isEmpty(dataForPopup) && (
        <Portal>
          <CancelOfferPopup popupName={cancelOfferPopupName} data={dataForPopup} />
        </Portal>
      )}
      {rejectOfferPopupData && !isEmpty(dataForPopup) && (
        <Portal>
          <RejectOfferPopup popupName={rejectOfferPopupName} data={dataForPopup} />
        </Portal>
      )}
      <DealsHeading>
        <h1>Open deals</h1>
        {dealsList.length > 0 && (
          <SelectionToggle>
            <label htmlFor="bulk-offers">Select offers</label>
            <SwitchToggle
              id="bulk-offers"
              toggled={selectionMode}
              isDisabled={busy}
              onChange={() => {
                setSelectionMode(!selectionMode);
                setSelected([]);
                setConfirmation(null);
              }}
            />
          </SelectionToggle>
        )}
      </DealsHeading>
      {!dealsList.length && (
        <StyledWrapper>
          <StyledIcon />
          <StyledLabel>
            No open deals yet, search for players and start a deal
          </StyledLabel>
          <StyledLink to="/transfers/search-players">Search Players</StyledLink>
        </StyledWrapper>
      )}
      {selectionMode && dealsList.length > 0 && (
        <BatchBar aria-label="Offer selection" aria-busy={busy}>
          <StyledButton
            basic
            small
            disabled={busy || !eligible.length}
            onClick={() =>
              setSelected(
                selectedOffers.length === eligible.length
                  ? []
                  : eligible.map((offer) => offer._id)
              )
            }
          >
            {eligible.length > 0 && selectedOffers.length === eligible.length
              ? 'Clear selection'
              : 'Select all incoming'}
          </StyledButton>
          <span>{selectedOffers.length} selected</span>
          <div>
            <StyledButton
              danger
              small
              disabled={busy || !selectedOffers.length}
              onClick={() => {
                setResults([]);
                setConfirmation('reject');
              }}
            >
              Decline
            </StyledButton>
            <StyledButton
              primary
              small
              disabled={busy || !selectedOffers.length || multipleOffersForPlayer}
              onClick={() => {
                setResults([]);
                setConfirmation('accept');
              }}
            >
              Accept
            </StyledButton>
          </div>
          {multipleOffersForPlayer && <p>Select only one offer per player to accept.</p>}
        </BatchBar>
      )}
      {confirmation && (
        <Portal>
          <BatchOfferPopup
            action={confirmation}
            offers={selectedOffers}
            players={[...myTeam, ...allPlayersList]}
            busy={busy}
            progress={progress}
            results={results}
            onConfirm={runBatch}
            canConfirm={
              selectedOffers.length > 0 &&
              !(confirmation === 'accept' && multipleOffersForPlayer)
            }
            onClose={() => {
              if (!busy) {
                setConfirmation(null);
                setResults([]);
              }
            }}
          />
        </Portal>
      )}
      <StyledViewDealsContainer>
        {dealsList.length > 0 &&
          dealsList.map((offer, index) => {
            const playerId =
              offer?.player?._id ||
              offer?.player_id ||
              (typeof offer?.player === 'string' ? offer.player : null);
            if (!playerId) return null;
            const playerFromList =
              allPlayersList.find((player) => player._id === playerId) ||
              myTeam.find((player) => player._id === playerId);
            if (isEmpty(playerFromList)) return null;
            const sendingTeamId = offer?.sending_team?._id;
            if (!sendingTeamId) return null;
            const {
              potential_min: minPotentialValue,
              potential_max: maxPotentialValue,
              rating_range: ratingRange,
              salary,
            } = playerFromList;
            const ownPlayer = myTeam.find((player) => player._id === playerId);
            const listedSource =
              ownPlayer && typeof ownPlayer.listed === 'boolean'
                ? ownPlayer
                : playerFromList;
            return (
              <DealSelection
                key={offer._id || `${playerId}-${index}`}
                $selected={selectionMode && selected.includes(offer._id)}
                $unavailable={
                  selectionMode && !eligible.some((item) => item._id === offer._id)
                }
                $selecting={selectionMode}
              >
                {selectionMode && (
                  <SelectionCheck>
                    <input
                      type="checkbox"
                      aria-label={`Select ${offer.player.name}`}
                      disabled={busy || !eligible.some((item) => item._id === offer._id)}
                      checked={selected.includes(offer._id)}
                      onChange={() => toggle(offer._id)}
                    />
                    <span aria-hidden="true" />
                  </SelectionCheck>
                )}
                <div inert={selectionMode || busy ? '' : undefined}>
                  <TransfersPlayerCard
                    cardType={getTransfersOfferType(
                      teamId,
                      sendingTeamId,
                      offer.category
                    )}
                    className={`${index}index`}
                    offer={offer}
                    type={offer.type}
                    key={offer._id || `${playerId}-${index}`}
                    selectionMode={selectionMode}
                    selectPlayer={selectPlayer}
                    setSelectPlayer={setSelectPlayer}
                    minPotentialValue={minPotentialValue}
                    maxPotentialValue={maxPotentialValue}
                    ratingRange={ratingRange}
                    salary={salary}
                    listed={
                      typeof listedSource.listed === 'boolean'
                        ? listedSource.listed
                        : undefined
                    }
                    sidebarPlayer={playerFromList}
                  />
                </div>
              </DealSelection>
            );
          })}
      </StyledViewDealsContainer>
    </StyledContainer>
  );
};

export default React.memo(ViewDealsContainer);
